import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import { useCart } from "@/stores/cartStore";
import { supabase } from "@/lib/supabase";
import {
  type Customer,
  type CustomerAddress,
  useCustomerAddresses,
} from "@/lib/db";
import { useCreateOrder, type PaymentMethod } from "@/lib/orders";
import { getDeliverySettings, listDeliveryDates, formatDateDe, formatDateIso } from "@/lib/calendar";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export default function CheckoutPage() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();
  const items = useCart((s) => s.items);
  const totalCents = useCart((s) => s.totalCents());
  const clear = useCart((s) => s.clear);
  const create = useCreateOrder();

  // Eigener Customer (über profile_id)
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loadingCust, setLoadingCust] = useState(true);
  useEffect(() => {
    if (!user) return;
    supabase.from("customers").select("*").eq("profile_id", user.id).maybeSingle()
      .then(({ data }) => { setCustomer(data as Customer | null); setLoadingCust(false); });
  }, [user]);

  const { data: addresses = [] } = useCustomerAddresses(customer?.id);
  const [addressId, setAddressId] = useState<string>("");
  useEffect(() => {
    if (!addressId && addresses.length) {
      setAddressId(addresses.find((a) => a.is_default)?.id ?? addresses[0].id);
    }
  }, [addresses, addressId]);

  // Liefertage
  const [dates, setDates] = useState<Date[]>([]);
  const [deliveryDate, setDeliveryDate] = useState<string>("");
  useEffect(() => {
    getDeliverySettings().then((s) => {
      const list = listDeliveryDates(new Date(), s, 10);
      setDates(list);
      setDeliveryDate(formatDateIso(list[0]));
    });
  }, []);

  const [payment, setPayment] = useState<PaymentMethod>("cash");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="card text-center space-y-3">
        <p>Ihr Korb ist leer.</p>
        <Link to="/shop" className="btn-primary inline-block">Zum Hofladen</Link>
      </div>
    );
  }

  if (loadingCust) return <div className="card">Lade…</div>;
  if (!customer) {
    return (
      <div className="card text-center text-hof-600 space-y-3">
        <p>Es ist noch kein Kundenkonto verknüpft. Bitte wenden Sie sich an info@zuhause-os.de.</p>
        <Link to="/shop" className="btn-secondary inline-block">Zurück</Link>
      </div>
    );
  }

  async function onSubmit() {
    setErr(null);
    if (!addressId || !deliveryDate) {
      setErr("Bitte Lieferadresse und Liefertag auswählen.");
      return;
    }
    try {
      const order = await create.mutateAsync({
        customer_id: customer!.id,
        address_id: addressId,
        delivery_date: deliveryDate,
        payment_method: payment,
        source: "app",
        notes: notes.trim() || undefined,
        items: items.map((i) => ({
          product_id: i.product_id, qty: i.qty, unit_price_cents: i.unit_price_cents,
        })),
      });
      clear();
      nav(`/shop/bestellungen/${order.id}`, { replace: true });
    } catch (ex) {
      setErr((ex as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-brand font-bold text-wappen-700">Zur Kasse</h1>

      {/* Adresse */}
      <section className="card space-y-3">
        <h2 className="font-semibold text-hof-800">Lieferadresse</h2>
        {addresses.length === 0 ? (
          <p className="text-hof-600 text-sm">
            Keine Adresse gespeichert.{" "}
            <Link to="/shop/konto" className="text-wiese-700 underline">Adresse anlegen</Link>
          </p>
        ) : (
          <div className="space-y-2">
            {addresses.map((a) => <AddressOption key={a.id} a={a} checked={addressId === a.id} onSelect={() => setAddressId(a.id)} />)}
          </div>
        )}
      </section>

      {/* Liefertag */}
      <section className="card space-y-3">
        <h2 className="font-semibold text-hof-800">Liefertag</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {dates.map((d) => {
            const iso = formatDateIso(d);
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setDeliveryDate(iso)}
                className={`text-left p-3 rounded-xl border min-h-touch ${
                  deliveryDate === iso
                    ? "border-wiese-500 bg-wiese-50 text-wiese-800"
                    : "border-hof-200 hover:bg-hof-50"
                }`}
              >
                {formatDateDe(d)}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-hof-500">Bestellungen nach 16:00 Uhr rutschen auf den übernächsten Liefertag.</p>
      </section>

      {/* Zahlart */}
      <section className="card space-y-3">
        <h2 className="font-semibold text-hof-800">Zahlart</h2>
        <div className="space-y-2">
          <PayOption value="cash"    label="Bar bei Lieferung" selected={payment} onSelect={setPayment} />
          <PayOption value="stripe"  label="Karte / Apple Pay (kommt bald)"  selected={payment} onSelect={setPayment} disabled />
          <PayOption value="paypal"  label="PayPal (kommt bald)"             selected={payment} onSelect={setPayment} disabled />
          <PayOption value="sepa"    label="SEPA-Lastschrift (kommt bald)"   selected={payment} onSelect={setPayment} disabled />
        </div>
      </section>

      {/* Notiz */}
      <section className="card space-y-2">
        <label className="font-semibold text-hof-800">Hinweis für den Fahrer (optional)</label>
        <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </section>

      {/* Summe + Button */}
      <section className="card flex items-center justify-between">
        <div>
          <div className="text-sm text-hof-500">Gesamt</div>
          <div className="text-3xl font-bold text-wiese-700">{fmt.format(totalCents / 100)}</div>
        </div>
        <button onClick={onSubmit} className="btn-primary text-lg py-4 px-6" disabled={create.isPending}>
          {create.isPending ? "Bestelle…" : "Verbindlich bestellen"}
        </button>
      </section>

      {err && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">{err}</div>}
    </div>
  );
}

function AddressOption({ a, checked, onSelect }: { a: CustomerAddress; checked: boolean; onSelect: () => void }) {
  return (
    <label className={`block p-3 rounded-xl border cursor-pointer ${
      checked ? "border-wiese-500 bg-wiese-50" : "border-hof-200 hover:bg-hof-50"
    }`}>
      <div className="flex items-start gap-3">
        <input type="radio" checked={checked} onChange={onSelect} className="mt-1 w-5 h-5" />
        <div className="flex-1">
          <div className="font-medium">{a.label ?? "Adresse"}{a.is_default && <span className="ml-2 pill-success text-xs">Standard</span>}</div>
          <div className="text-sm text-hof-700">{a.street}, {a.zip} {a.city}</div>
          {a.delivery_notes && <div className="text-xs text-hof-500">Hinweis: {a.delivery_notes}</div>}
        </div>
      </div>
    </label>
  );
}

function PayOption({ value, label, selected, onSelect, disabled }: {
  value: PaymentMethod; label: string; selected: PaymentMethod;
  onSelect: (v: PaymentMethod) => void; disabled?: boolean;
}) {
  return (
    <label className={`flex items-center gap-3 p-3 rounded-xl border min-h-touch cursor-pointer ${
      disabled ? "opacity-40 cursor-not-allowed border-hof-100"
      : selected === value ? "border-wiese-500 bg-wiese-50" : "border-hof-200 hover:bg-hof-50"
    }`}>
      <input
        type="radio"
        checked={selected === value}
        onChange={() => !disabled && onSelect(value)}
        disabled={disabled}
        className="w-5 h-5"
      />
      <span>{label}</span>
    </label>
  );
}
